import type { DifficultyId } from '../game/combat/types/primitives'
import {
  applyDifficultyClear,
  emptyClears,
  starCountFromClears,
  type StageDifficultyClears
} from '../game/combat/campaign/stars'
import { resolveFirstClearRewards } from '../game/combat/campaign/firstClear'
import {
  getStage,
  nextStageId,
  listChapterStages,
  listChapters,
  getChapter,
  getChapterForStage
} from '../game/campaign'
import {
  chapterMaxStars,
  campaignMaxStars,
  MAIN_CAMPAIGN
} from '../game/campaign/registry'
import type { StageFirstClearRewards } from '../game/combat/campaign/firstClear'

export const CAMPAIGN_PROGRESS_V1_KEY = 'shapes-td.progress.v1'
/** Legacy Campaign V2 progress key — no longer read/written. */
export const CAMPAIGN_PROGRESS_V2_KEY = 'shapes-td.progress.v2'
export const CAMPAIGN_PROGRESS_STORAGE_KEY = 'shapes-td.progress.v3'
export const CAMPAIGN_PROGRESS_VERSION = 3 as const

/**
 * Campaign V3 migration policy (pre-production):
 * - Do NOT silently remap old 2-8 clears onto redesigned long Levels.
 * - Fresh campaign progress on first load of v3.
 * - Research / Ingots wallet lives in meta store and is retained.
 */
export const CAMPAIGN_V3_PROGRESS_RESET = true

/** Product policy: Easy clear does NOT unlock the next Level. Normal+ does. */
export const UNLOCK_REQUIRES_NORMAL_OR_HIGHER = true

/** One-time Stage completion Ingots bonuses (idempotent per chapter id). */
export const CHAPTER_COMPLETION_INGOTS: Record<string, number> = {
  'chapter-1': 50,
  'chapter-2': 100
}

/** @deprecated Use CHAPTER_COMPLETION_INGOTS['chapter-1'] */
export const CHAPTER_1_COMPLETION_INGOTS = CHAPTER_COMPLETION_INGOTS['chapter-1']!

export interface DifficultyBest {
  bestLives: number | null
  bestTime: number | null
}

export interface StageProgressEntry {
  stageId: string
  clears: StageDifficultyClears
  /** First-clear Ingots already granted per difficulty (idempotent). */
  firstClearGranted: StageDifficultyClears
  bestByDifficulty: Partial<Record<DifficultyId, DifficultyBest>>
  lastPlayedAt: number
}

export interface CampaignProgressStateV2 {
  version: typeof CAMPAIGN_PROGRESS_VERSION
  ingots: number
  /** Chapter ids that already received the chapter-completion Ingots bonus. */
  chapterCompletionGranted: Record<string, boolean>
  lastPlayedStageId: string | null
  stages: Record<string, StageProgressEntry>
}

export interface VictoryRecordInputV2 {
  stageId: string
  difficultyId: DifficultyId
  lives: number
  elapsedTime: number
  isPlaytest?: boolean
  now?: number
}

export interface VictoryRecordResult {
  recorded: boolean
  entry: StageProgressEntry | null
  newStars: number
  previousStars: number
  starCount: number
  clears: StageDifficultyClears
  ingotsGranted: number
  newlyClearedDifficulties: DifficultyId[]
  chapterCompleted: boolean
  chapterIngotsGranted: number
  nextStageUnlocked: boolean
  unlockedStageId: string | null
  newBestLives: boolean
  newBestTime: boolean
  totalIngots: number
}

function emptyState(): CampaignProgressStateV2 {
  return {
    version: CAMPAIGN_PROGRESS_VERSION,
    ingots: 0,
    chapterCompletionGranted: {},
    lastPlayedStageId: null,
    stages: {}
  }
}

function isClears(value: unknown): value is StageDifficultyClears {
  if (!value || typeof value !== 'object') return false
  const r = value as StageDifficultyClears
  return (
    typeof r.easy === 'boolean' &&
    typeof r.normal === 'boolean' &&
    typeof r.hard === 'boolean' &&
    typeof r.insane === 'boolean'
  )
}

function isStageEntry(value: unknown): value is StageProgressEntry {
  if (!value || typeof value !== 'object') return false
  const r = value as Partial<StageProgressEntry>
  return (
    typeof r.stageId === 'string' &&
    isClears(r.clears) &&
    isClears(r.firstClearGranted) &&
    typeof r.lastPlayedAt === 'number' &&
    r.bestByDifficulty !== undefined &&
    typeof r.bestByDifficulty === 'object'
  )
}

function sanitizeState(raw: unknown): CampaignProgressStateV2 {
  if (!raw || typeof raw !== 'object') return emptyState()
  const record = raw as Partial<CampaignProgressStateV2>
  if (record.version !== CAMPAIGN_PROGRESS_VERSION) return emptyState()
  const stages: Record<string, StageProgressEntry> = {}
  if (record.stages && typeof record.stages === 'object') {
    for (const [key, value] of Object.entries(record.stages)) {
      if (!isStageEntry(value)) continue
      if (value.stageId !== key) continue
      stages[key] = {
        stageId: value.stageId,
        clears: { ...value.clears },
        firstClearGranted: { ...value.firstClearGranted },
        bestByDifficulty: { ...value.bestByDifficulty },
        lastPlayedAt: value.lastPlayedAt
      }
    }
  }
  return {
    version: CAMPAIGN_PROGRESS_VERSION,
    ingots: Math.max(0, Math.floor(Number(record.ingots) || 0)),
    chapterCompletionGranted:
      record.chapterCompletionGranted && typeof record.chapterCompletionGranted === 'object'
        ? { ...record.chapterCompletionGranted }
        : {},
    lastPlayedStageId:
      typeof record.lastPlayedStageId === 'string' ? record.lastPlayedStageId : null,
    stages
  }
}

function ensureStage(
  state: CampaignProgressStateV2,
  stageId: string,
  now: number
): StageProgressEntry {
  const existing = state.stages[stageId]
  if (existing) return existing
  return {
    stageId,
    clears: emptyClears(),
    firstClearGranted: emptyClears(),
    bestByDifficulty: {},
    lastPlayedAt: now
  }
}

/** Normal or higher clear unlocks progression (product policy). */
export function clearsUnlockNext(clears: StageDifficultyClears): boolean {
  if (!UNLOCK_REQUIRES_NORMAL_OR_HIGHER) {
    return clears.easy || clears.normal || clears.hard || clears.insane
  }
  return clears.normal || clears.hard || clears.insane
}

/**
 * Chapter unlocked when prior chapter is completed (or chapter is first in campaign).
 * Chapter 1 is always unlocked.
 */
export function isChapterUnlocked(
  chapterId: string,
  state: CampaignProgressStateV2 = campaignProgress.load()
): boolean {
  const chapters = listChapters(MAIN_CAMPAIGN.id)
  const idx = chapters.findIndex((c) => c.id === chapterId)
  if (idx < 0) return false
  if (idx === 0) return true
  const prev = chapters[idx - 1]!
  return isChapterCompleted(prev.id, state)
}

export function isStageUnlocked(
  stageId: string,
  state: CampaignProgressStateV2 = campaignProgress.load()
): boolean {
  const chapter = getChapterForStage(stageId)
  if (!chapter) return false
  if (!isChapterUnlocked(chapter.id, state)) return false
  const idx = chapter.stageIds.indexOf(stageId)
  if (idx < 0) return false
  if (idx === 0) return true
  const prevId = chapter.stageIds[idx - 1]!
  const prev = state.stages[prevId]
  if (!prev) return false
  return clearsUnlockNext(prev.clears)
}

/** Stars for one chapter. Defaults to Chapter 1 for backwards-compatible call sites. */
export function chapterStarTotal(
  state: CampaignProgressStateV2,
  chapterId: string = 'chapter-1'
): {
  earned: number
  max: number
} {
  const stages = listChapterStages(chapterId)
  let earned = 0
  for (const stage of stages) {
    const entry = state.stages[stage.id]
    earned += entry ? starCountFromClears(entry.clears) : 0
  }
  return { earned, max: chapterMaxStars(chapterId) || stages.length * 4 }
}

export function campaignStarTotal(state: CampaignProgressStateV2): {
  earned: number
  max: number
} {
  let earned = 0
  for (const chapter of listChapters()) {
    earned += chapterStarTotal(state, chapter.id).earned
  }
  return { earned, max: campaignMaxStars() }
}

/**
 * Chapter complete when its finale stage is cleared on Normal or higher.
 * Uses last stage id from ChapterDefinition — not hardcoded ordinals.
 */
export function isChapterCompleted(
  chapterId: string,
  state: CampaignProgressStateV2
): boolean {
  const chapter = getChapter(chapterId)
  if (!chapter || chapter.stageIds.length === 0) return false
  const finaleId = chapter.stageIds[chapter.stageIds.length - 1]!
  const finale = state.stages[finaleId]
  if (!finale) return false
  return clearsUnlockNext(finale.clears)
}

export function createCampaignProgressStore(storage?: Storage) {
  const resolve = (): Storage | null => {
    if (storage) return storage
    if (typeof localStorage === 'undefined') return null
    return localStorage
  }

  const read = (): CampaignProgressStateV2 => {
    const store = resolve()
    if (!store) return emptyState()
    try {
      const raw = store.getItem(CAMPAIGN_PROGRESS_STORAGE_KEY)
      if (!raw) return emptyState()
      return sanitizeState(JSON.parse(raw) as unknown)
    } catch {
      return emptyState()
    }
  }

  const write = (state: CampaignProgressStateV2): void => {
    const store = resolve()
    if (!store) return
    try {
      store.setItem(CAMPAIGN_PROGRESS_STORAGE_KEY, JSON.stringify(state))
    } catch {
      // quota / private mode
    }
  }

  return {
    load(): CampaignProgressStateV2 {
      return read()
    },

    getIngots(): number {
      return read().ingots
    },

    spendIngots(amount: number): { ok: true; ingotsAfter: number } | { ok: false } {
      const cost = Math.max(0, Math.floor(amount))
      if (cost <= 0) return { ok: true, ingotsAfter: read().ingots }
      const state = read()
      if (state.ingots < cost) return { ok: false }
      state.ingots -= cost
      write(state)
      return { ok: true, ingotsAfter: state.ingots }
    },

    getStage(stageId: string): StageProgressEntry | null {
      return read().stages[stageId] ?? null
    },

    isUnlocked(stageId: string): boolean {
      return isStageUnlocked(stageId, read())
    },

    isChapterUnlocked(chapterId: string): boolean {
      return isChapterUnlocked(chapterId, read())
    },

    recordVictory(input: VictoryRecordInputV2): VictoryRecordResult {
      const none: VictoryRecordResult = {
        recorded: false,
        entry: null,
        newStars: 0,
        previousStars: 0,
        starCount: 0,
        clears: emptyClears(),
        ingotsGranted: 0,
        newlyClearedDifficulties: [],
        chapterCompleted: false,
        chapterIngotsGranted: 0,
        nextStageUnlocked: false,
        unlockedStageId: null,
        newBestLives: false,
        newBestTime: false,
        totalIngots: read().ingots
      }
      if (input.isPlaytest) return none
      const stage = getStage(input.stageId)
      if (!stage) return none

      const now = input.now ?? Date.now()
      const state = read()
      const prev = ensureStage(state, input.stageId, now)
      const previousStars = starCountFromClears(prev.clears)
      const clears = applyDifficultyClear(prev.clears, input.difficultyId)
      const starCount = starCountFromClears(clears)
      const newStars = Math.max(0, starCount - previousStars)

      const rewards: StageFirstClearRewards = stage.firstClearRewards
      const firstClear = resolveFirstClearRewards(
        prev.firstClearGranted,
        input.difficultyId,
        rewards
      )

      const lives = Math.max(0, Math.floor(input.lives))
      const time = input.elapsedTime > 0 ? input.elapsedTime : null
      const prevBest = prev.bestByDifficulty[input.difficultyId]
      const newBestLives = prevBest?.bestLives == null || lives > prevBest.bestLives
      const newBestTime =
        time != null && (prevBest?.bestTime == null || time < prevBest.bestTime)

      const bestByDifficulty: StageProgressEntry['bestByDifficulty'] = {
        ...prev.bestByDifficulty,
        [input.difficultyId]: {
          bestLives: newBestLives ? lives : (prevBest?.bestLives ?? lives),
          bestTime: newBestTime ? time : (prevBest?.bestTime ?? time)
        }
      }

      let ingots = state.ingots + firstClear.totalRewardAmount
      let chapterIngotsGranted = 0
      let chapterCompleted = false

      const entry: StageProgressEntry = {
        stageId: input.stageId,
        clears,
        firstClearGranted: firstClear.granted,
        bestByDifficulty,
        lastPlayedAt: now
      }
      state.stages[input.stageId] = entry
      state.lastPlayedStageId = input.stageId

      const probeState = { ...state, stages: { ...state.stages, [input.stageId]: entry } }
      if (isChapterCompleted(stage.chapterId, probeState)) {
        chapterCompleted = true
        const bonus = CHAPTER_COMPLETION_INGOTS[stage.chapterId] ?? 0
        if (bonus > 0 && !state.chapterCompletionGranted[stage.chapterId]) {
          chapterIngotsGranted = bonus
          ingots += chapterIngotsGranted
          state.chapterCompletionGranted[stage.chapterId] = true
        }
      }

      state.ingots = ingots
      write(state)

      const unlockedStageId = nextStageId(input.stageId)
      const nextStageUnlocked =
        clearsUnlockNext(clears) &&
        unlockedStageId !== null &&
        isStageUnlocked(unlockedStageId, state)

      return {
        recorded: true,
        entry,
        newStars,
        previousStars,
        starCount,
        clears,
        ingotsGranted: firstClear.totalRewardAmount,
        newlyClearedDifficulties: firstClear.newlyCleared,
        chapterCompleted,
        chapterIngotsGranted,
        nextStageUnlocked,
        unlockedStageId: nextStageUnlocked ? unlockedStageId : null,
        newBestLives,
        newBestTime,
        totalIngots: ingots
      }
    },

    recordDefeat(input: {
      stageId: string
      isPlaytest?: boolean
      now?: number
    }): { recorded: boolean } {
      if (input.isPlaytest) return { recorded: false }
      if (!getStage(input.stageId)) return { recorded: false }
      const now = input.now ?? Date.now()
      const state = read()
      const prev = ensureStage(state, input.stageId, now)
      state.stages[input.stageId] = { ...prev, lastPlayedAt: now }
      state.lastPlayedStageId = input.stageId
      write(state)
      return { recorded: true }
    },

    resetProgress(): void {
      write(emptyState())
    }
  }
}

export const campaignProgress = createCampaignProgressStore()

export function formatBestTime(seconds: number | null | undefined): string {
  if (seconds == null || !(seconds > 0)) return '—'
  const total = Math.floor(seconds)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function formatStarsAria(clears: StageDifficultyClears): string {
  const n = starCountFromClears(clears)
  return `${n} of 4 stars`
}

export function starSlots(clears: StageDifficultyClears): Array<{
  difficulty: DifficultyId
  filled: boolean
}> {
  return [
    { difficulty: 'easy', filled: clears.easy },
    { difficulty: 'normal', filled: clears.normal },
    { difficulty: 'hard', filled: clears.hard },
    { difficulty: 'insane', filled: clears.insane }
  ]
}
